/* Home · Constitutional Refactor · Batch C3a (surface) + C3c (behaviour)
 * =======================================================================
 * The single canonical Home surface.
 *
 * C3a · calm surface with one PrimaryAction.
 * C3c · every async action now flows through useTruthState +
 *        TruthReceipt. No bespoke toasts. No parallel receipts.
 *        Failed loads stay visible until dismissed or retried.
 *
 * Constitutional articles served:
 *   • 3 (Truth Above Convenience) — every action ends knowably
 *   • 4 (Better Decisions) — one lead
 *   • 7 (Reduce Cognitive Load) — nothing to memorise
 *   • 10 (Leave People Better) — clarity beats completeness
 */
import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { PlayCircle } from "lucide-react";
import { PrimaryAction } from "../components/PrimaryAction";
import { TruthReceipt } from "../components/TruthReceipt";
import { useTruthState } from "../hooks/useTruthState";
import MyBookings from "./MyBookings";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function Home() {
  const { user } = useAuth();
  const [primary, setPrimary] = useState(null);
  const navigate = useNavigate();
  const truth = useTruthState();
  // Stable refs so the load effect below has an [] dependency and
  // can never re-fire on state transitions (which would otherwise
  // create an infinite PROCESSING → COMPLETED → null → PROCESSING
  // loop as the memoized `truth` object gets a new identity on
  // every transition — see C3c regression Aug 2026).
  const runRef = useRef(truth.run);
  const setStateRef = useRef(truth.setState);
  useEffect(() => { runRef.current = truth.run; }, [truth.run]);
  useEffect(() => { setStateRef.current = truth.setState; }, [truth.setState]);

  const load = useCallback(async () => {
    await runRef.current(
      async () => {
        const r = await axios.get(`${API}/api/home/primary`, {
          withCredentials: true,
        });
        setPrimary(r.data);
        return r.data;
      },
      {
        // Truth Doctrine · every terminal state carries an honest
        // sentence. `completed` is silenced by the receipt (see
        // dismiss handler below) so it only whispers.
        completedMessage: null,
        failedMessage:    "Couldn't load your primary action.",
      },
    );
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const go = () => {
    if (primary?.destination) navigate(primary.destination);
  };

  // Completed loads auto-clear the receipt so the Home surface
  // remains calm. Failed loads stay visible until Retry/Dismiss —
  // never silent (Truth Doctrine). Depend ONLY on `truth.state`
  // (a stable primitive) so we don't re-fire on every render.
  useEffect(() => {
    if (truth.state === "completed") {
      const t = setTimeout(() => setStateRef.current(null), 400);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [truth.state]);

  return (
    <div
      data-testid="home-surface"
      className="min-h-full flex flex-col items-center justify-start px-4 py-5"
    >
      <div className="w-full max-w-xl space-y-4">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">
            NDG
          </p>
          <h1
            data-testid="home-title"
            className="mt-2 text-3xl font-black text-slate-900 dark:text-white"
          >
            Your calendar
          </h1>
          <p className="mt-1 text-sm text-slate-500">{(user?.founder_mode_subject_name || user?.name || "").split(" ")[0]}{(user?.founder_mode_subject_name || user?.name) ? " · " : ""}Your lessons and preparation</p>
          </div>
          <Link to="/learn/book" data-testid="home-book-lesson" className="min-h-11 inline-flex items-center shrink-0 rounded-xl bg-blue-700 px-3 text-sm font-bold text-white">Book lesson</Link>
        </header>

        <MyBookings />

        {primary && (
          <PrimaryAction
            onClick={go}
            icon={PlayCircle}
            testid="primary-action"
          >
            {primary.label}
          </PrimaryAction>
        )}

        <TruthReceipt
          state={truth.state}
          message={truth.message}
          onRetry={load}
          onDismiss={() => truth.setState(null)}
        />
      </div>
    </div>
  );
}

